import { motion } from 'framer-motion';
import Button from '@mui/material/Button';
import { Mountain, Sparkles, CalendarDays } from 'lucide-react';
import { WellnessSection } from './wellnessSection';
import { useNavigate } from 'react-router-dom';
import { Trees, HeartHandshake, BadgeCheck } from 'lucide-react';

//import { WellnessProgramCard } from './wellnessProgramCard';
export function RelaxationPackage() {
  const navigate = useNavigate();

  return (
    <>
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden mt-20 lg:mt-20">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1706859047968-c5d77c526695?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtb3VudGFpbiUyMG1lZGl0YXRpb24lMjBzdW5yaXNlfGVufDF8fHx8MTc2MTgxOTkzN3ww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-teal-900/60 to-green-900/70" />
      </div>

      {/* Floating particles effect */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              y: [null, -100],
              opacity: [0.2, 0.5, 0],
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              delay: Math.random() * 5,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          {/* Badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8 mt-8"
          >
            <Mountain className="w-5 h-5 text-emerald-300" />
            <span className="text-white">Mountain Wellness Retreats</span>
          </motion.div>

          <motion.h1
            className="text-white mb-6 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
            Find Deep Healing in Nature's Embrace
          </motion.h1>
          
          <motion.p
            className="text-xl text-emerald-50 mb-4 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
          >
            Escape to pristine mountain sanctuaries where ancient forests meet mindful practices. 
            Rediscover yourself through transformative wellness programs designed for complete rejuvenation.
          </motion.p>

          <motion.p
            className="text-lg text-emerald-100/80 mb-12 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
          >
            Reboot your mind • Heal your body • Restore your spirit
          </motion.p>

          <motion.div
            className="flex flex-wrap gap-4 justify-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="hidden md:block">
              <Button 
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: 'white',
                  color: 'rgba(4, 120, 87, 0.9)', // emerald-700 approx
                  px: 8,
                  py: 3,
                  boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                  '&:hover': { backgroundColor: '#D1FAE5' } // emerald-50 approx
                }}
                startIcon={<Sparkles size={20} />}
                onClick={() => navigate('/wellnessinfopage')}
              >
              Corporate Programs
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                variant="outlined" 
                size="large"
                sx={{
                  borderWidth: 2,
                  borderColor: 'white',
                  color: 'white',
                  backdropFilter: 'blur(6px)',
                  px: 8,
                  py: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  }
                }}
                startIcon={<CalendarDays size={20} />}
                /*onClick={() => {
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  }}*/
  onClick={() => navigate('/retreat-calendar')}
              >
                Retreat Calendar
              </Button>
            </motion.div>
          </motion.div>

          {/* Wellness Promises */}
      <motion.div
  className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ delay: 1.3, duration: 0.8 }}
>
  {[
    {
      title: 'Nature Immersion',
      description: 'Surrounded by ancient mountains and pristine forests',
      icon: Trees,
    },
    {
      title: 'Deep Healing',
      description: 'Holistic programs for mind, body, and soul transformation',
      icon: HeartHandshake,
    },
    {
      title: 'Expert Guidance',
      description: 'Certified wellness practitioners and mindfulness coaches',
      icon: BadgeCheck,
    },
  ].map((promise, index) => {
    const Icon = promise.icon;

    return (
   <motion.div
  key={index}
  className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 text-center"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 1.4 + index * 0.1, duration: 0.6 }}
  whileHover={{
    scale: 1.05,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  }}
>
  <div className="mb-4 flex items-center justify-center text-emerald-300">
    <Icon size={28} strokeWidth={1.75} />
  </div>

  <h3 className="text-white mb-2">{promise.title}</h3>
  <p className="text-emerald-100/80 text-sm leading-relaxed max-w-xs mx-auto">
    {promise.description}
  </p>
</motion.div>
    );
  })}
</motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <motion.div
            className="w-1 h-2 bg-white rounded-full"
            animate={{
              y: [0, 12, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </motion.div>
    </div>
 

    </>
  );
}
